// Tester create / edit form — switches fields based on `kind`. The `new`
// route reuses this component with no id; the edit route loads existing
// data and pre-fills.
//
// mig 0010 added Tester Category (Third Party / Internal). When set to
// Third Party, the W9 / Bank Info / Citizenship / Mailing Address /
// BAC Payee / Personal Bank for Payment / Notes / Tester Status fields
// are surfaced. When Internal, those fields stay hidden.

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { lookups, testers } from '@/lib/api';
import type { TesterCategory, TesterKind, W9Status } from '@frt/shared-types';

interface FormState {
  kind: TesterKind;
  tester_category: TesterCategory;
  tester_status_id: number | '';
  first_name: string;
  last_name: string;
  short_name: string;
  business_name: string;
  short_business_name: string;
  opened_by_first_name: string;
  opened_by_last_name: string;
  opened_by_initials: string;
  email: string;
  mobile_phone: string;
  mailing_address: string;
  citizenship_id: number | '';
  w9: W9Status;
  bank_info_id: number | '';
  bac_payee: string;
  personal_bank_for_payment: string;
  notes: string;
  // user-edited flags so auto-derive doesn't clobber explicit input
  short_name_dirty: boolean;
  opened_by_initials_dirty: boolean;
}

const EMPTY: FormState = {
  kind: 'person',
  tester_category: 'internal',
  tester_status_id: '',
  first_name: '',
  last_name: '',
  short_name: '',
  business_name: '',
  short_business_name: '',
  opened_by_first_name: '',
  opened_by_last_name: '',
  opened_by_initials: '',
  email: '',
  mobile_phone: '',
  mailing_address: '',
  citizenship_id: '',
  w9: 'na',
  bank_info_id: '',
  bac_payee: '',
  personal_bank_for_payment: '',
  notes: '',
  short_name_dirty: false,
  opened_by_initials_dirty: false,
};

function deriveShortName(first: string, last: string): string {
  return `${first.trim()}${(last.trim()[0] ?? '').toUpperCase()}`;
}
function deriveOpenedByInitials(first: string, last: string): string {
  return `${(first.trim()[0] ?? '').toUpperCase()}${(last.trim()[0] ?? '').toUpperCase()}`;
}

export function TesterFormPage({ mode }: { mode: 'create' | 'edit' }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id: idStr } = useParams({ strict: false }) as { id?: string };
  const id = mode === 'edit' ? Number(idStr) : null;

  const { data: existing } = useQuery({
    queryKey: ['tester', id],
    queryFn: () => testers.get(id!),
    enabled: mode === 'edit' && Number.isFinite(id),
  });
  const { data: testerStatuses } = useQuery({
    queryKey: ['lookups', 'tester-status-options'],
    queryFn: () => lookups.testerStatusOptions.list(),
  });
  const { data: bankInfos } = useQuery({
    queryKey: ['lookups', 'bank-info-options'],
    queryFn: () => lookups.bankInfoOptions.list(),
  });
  const { data: citizenships } = useQuery({
    queryKey: ['lookups', 'citizenship-options'],
    queryFn: () => lookups.citizenshipOptions.list(),
  });

  const [form, setForm] = useState<FormState>(EMPTY);
  useEffect(() => {
    if (existing) {
      setForm({
        kind: existing.kind,
        tester_category: existing.tester_category ?? 'internal',
        tester_status_id: existing.tester_status_id ?? '',
        first_name: existing.first_name ?? '',
        last_name: existing.last_name ?? '',
        short_name: existing.short_name ?? '',
        business_name: existing.business_name ?? '',
        short_business_name: existing.short_business_name ?? '',
        opened_by_first_name: existing.opened_by_first_name ?? '',
        opened_by_last_name: existing.opened_by_last_name ?? '',
        opened_by_initials: existing.opened_by_initials ?? '',
        email: existing.email ?? '',
        mobile_phone: existing.mobile_phone ?? '',
        mailing_address: existing.mailing_address ?? '',
        citizenship_id: existing.citizenship_id ?? '',
        w9: existing.w9 ?? 'na',
        bank_info_id: existing.bank_info_id ?? '',
        bac_payee: existing.bac_payee ?? '',
        personal_bank_for_payment: existing.personal_bank_for_payment ?? '',
        notes: existing.notes ?? '',
        short_name_dirty: true,
        opened_by_initials_dirty: true,
      });
    }
  }, [existing]);

  const previewShortName = useMemo(() => {
    if (form.short_name_dirty) return form.short_name;
    if (form.kind !== 'person') return '';
    if (!form.first_name || !form.last_name) return '';
    return deriveShortName(form.first_name, form.last_name);
  }, [form]);
  const previewOpenedBy = useMemo(() => {
    if (form.opened_by_initials_dirty) return form.opened_by_initials;
    if (form.kind !== 'business') return '';
    if (!form.opened_by_first_name || !form.opened_by_last_name) return '';
    return deriveOpenedByInitials(form.opened_by_first_name, form.opened_by_last_name);
  }, [form]);

  const isThirdParty = form.tester_category === 'third_party';

  const buildPayload = () => ({
    tester_category: form.tester_category,
    tester_status_id: form.tester_status_id || null,
    first_name: form.kind === 'person' ? form.first_name || null : null,
    last_name: form.kind === 'person' ? form.last_name || null : null,
    short_name:
      form.kind === 'person'
        ? form.short_name_dirty && form.short_name
          ? form.short_name
          : null
        : null,
    business_name: form.kind === 'business' ? form.business_name || null : null,
    short_business_name:
      form.kind === 'business' ? form.short_business_name || null : null,
    opened_by_first_name:
      form.kind === 'business' ? form.opened_by_first_name || null : null,
    opened_by_last_name:
      form.kind === 'business' ? form.opened_by_last_name || null : null,
    opened_by_initials:
      form.kind === 'business'
        ? form.opened_by_initials_dirty && form.opened_by_initials
          ? form.opened_by_initials
          : null
        : null,
    email: form.email || null,
    mobile_phone: form.mobile_phone || null,
    // Third-Party-only fields write null when not surfaced.
    mailing_address: isThirdParty ? form.mailing_address || null : null,
    citizenship_id: isThirdParty ? (form.citizenship_id || null) : null,
    w9: isThirdParty ? form.w9 : 'na' as W9Status,
    bank_info_id: isThirdParty ? (form.bank_info_id || null) : null,
    bac_payee: isThirdParty ? form.bac_payee || null : null,
    personal_bank_for_payment: isThirdParty ? form.personal_bank_for_payment || null : null,
    notes: form.notes || null,
  });

  const create = useMutation({
    mutationFn: () => testers.create({ kind: form.kind, ...buildPayload() }),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['testers'] });
      void navigate({ to: '/testers/$id', params: { id: String(created.id) } });
    },
  });

  const update = useMutation({
    mutationFn: () => testers.update(id!, buildPayload()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tester', id] });
      void queryClient.invalidateQueries({ queryKey: ['testers'] });
    },
  });

  const error = create.error ?? update.error;

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold text-slate-900">
        {mode === 'create' ? 'New Tester' : 'Edit Tester'}
      </h1>
      <Card>
        <CardHeader>
          <CardTitle>Tester details</CardTitle>
        </CardHeader>
        <CardBody>
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (mode === 'create') create.mutate();
              else update.mutate();
            }}
          >
            <div className="flex gap-3 items-end flex-wrap">
              <label className="text-sm">
                <span className="block text-slate-600">Kind</span>
                <select
                  value={form.kind}
                  onChange={(e) =>
                    setForm({ ...form, kind: e.target.value as TesterKind })
                  }
                  disabled={mode === 'edit'}
                  className="rounded border border-slate-300 px-2 py-1"
                >
                  <option value="person">Person</option>
                  <option value="business">Business</option>
                </select>
              </label>
              <label className="text-sm">
                <span className="block text-slate-600">Tester Category</span>
                <select
                  value={form.tester_category}
                  onChange={(e) =>
                    setForm({ ...form, tester_category: e.target.value as TesterCategory })
                  }
                  className="rounded border border-slate-300 px-2 py-1"
                >
                  <option value="third_party">Third Party</option>
                  <option value="internal">Internal</option>
                </select>
              </label>
              <label className="text-sm">
                <span className="block text-slate-600">Tester Status</span>
                <select
                  value={form.tester_status_id}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      tester_status_id: e.target.value ? Number(e.target.value) : '',
                    })
                  }
                  className="rounded border border-slate-300 px-2 py-1 bg-white"
                >
                  <option value="">—</option>
                  {(testerStatuses?.data ?? [])
                    .filter((s) => s.active)
                    .map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                </select>
              </label>
            </div>

            {form.kind === 'person' ? (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label className="block text-sm">
                  <span className="block text-slate-600">First name *</span>
                  <Input
                    value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    required
                  />
                </label>
                <label className="block text-sm">
                  <span className="block text-slate-600">Last name *</span>
                  <Input
                    value={form.last_name}
                    onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                    required
                  />
                </label>
                <label className="block text-sm">
                  <span className="block text-slate-600">
                    Short name (auto:{' '}
                    <span className="font-mono">{previewShortName || '—'}</span>)
                  </span>
                  <Input
                    value={form.short_name}
                    onChange={(e) =>
                      setForm({ ...form, short_name: e.target.value, short_name_dirty: true })
                    }
                  />
                </label>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block text-sm">
                  <span className="block text-slate-600">Business name *</span>
                  <Input
                    value={form.business_name}
                    onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                    required
                  />
                </label>
                <label className="block text-sm">
                  <span className="block text-slate-600">Short business name *</span>
                  <Input
                    value={form.short_business_name}
                    onChange={(e) =>
                      setForm({ ...form, short_business_name: e.target.value })
                    }
                    required
                  />
                </label>
                <label className="block text-sm">
                  <span className="block text-slate-600">Opened by — first</span>
                  <Input
                    value={form.opened_by_first_name}
                    onChange={(e) =>
                      setForm({ ...form, opened_by_first_name: e.target.value })
                    }
                  />
                </label>
                <label className="block text-sm">
                  <span className="block text-slate-600">Opened by — last</span>
                  <Input
                    value={form.opened_by_last_name}
                    onChange={(e) =>
                      setForm({ ...form, opened_by_last_name: e.target.value })
                    }
                  />
                </label>
                <label className="block text-sm">
                  <span className="block text-slate-600">
                    Opened by — initials (auto:{' '}
                    <span className="font-mono">{previewOpenedBy || '—'}</span>)
                  </span>
                  <Input
                    value={form.opened_by_initials}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        opened_by_initials: e.target.value,
                        opened_by_initials_dirty: true,
                      })
                    }
                  />
                </label>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block text-sm">
                <span className="block text-slate-600">Email</span>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </label>
              <label className="block text-sm">
                <span className="block text-slate-600">Mobile phone</span>
                <Input
                  value={form.mobile_phone}
                  onChange={(e) => setForm({ ...form, mobile_phone: e.target.value })}
                />
              </label>
            </div>

            {/* Third-Party-only block */}
            {isThirdParty && (
              <div className="border-t border-slate-200 pt-3 space-y-3">
                <h3 className="text-sm font-medium text-slate-700">
                  Third-Party tester admin
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <label className="block text-sm">
                    <span className="block text-slate-600">W9</span>
                    <select
                      value={form.w9}
                      onChange={(e) => setForm({ ...form, w9: e.target.value as W9Status })}
                      className="block w-full rounded border border-slate-300 bg-white px-2 py-2 text-sm"
                    >
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                      <option value="na">NA</option>
                    </select>
                  </label>
                  <label className="block text-sm">
                    <span className="block text-slate-600">Bank Info</span>
                    <select
                      value={form.bank_info_id}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          bank_info_id: e.target.value ? Number(e.target.value) : '',
                        })
                      }
                      className="block w-full rounded border border-slate-300 bg-white px-2 py-2 text-sm"
                    >
                      <option value="">—</option>
                      {(bankInfos?.data ?? []).filter((o) => o.active).map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block text-sm">
                    <span className="block text-slate-600">Citizenship</span>
                    <select
                      value={form.citizenship_id}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          citizenship_id: e.target.value ? Number(e.target.value) : '',
                        })
                      }
                      className="block w-full rounded border border-slate-300 bg-white px-2 py-2 text-sm"
                    >
                      <option value="">—</option>
                      {(citizenships?.data ?? []).filter((o) => o.active).map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <label className="block text-sm">
                  <span className="block text-slate-600">Mailing Address</span>
                  <textarea
                    className="block w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm"
                    value={form.mailing_address}
                    onChange={(e) => setForm({ ...form, mailing_address: e.target.value })}
                    rows={2}
                  />
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="block text-sm">
                    <span className="block text-slate-600">BAC Payee (Yes/No)</span>
                    <select
                      value={form.bac_payee}
                      onChange={(e) => setForm({ ...form, bac_payee: e.target.value })}
                      className="block w-full rounded border border-slate-300 bg-white px-2 py-2 text-sm"
                    >
                      <option value="">—</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </label>
                  <label className="block text-sm">
                    <span className="block text-slate-600">Personal Bank For Payment</span>
                    <Input
                      value={form.personal_bank_for_payment}
                      onChange={(e) =>
                        setForm({ ...form, personal_bank_for_payment: e.target.value })
                      }
                    />
                  </label>
                </div>
              </div>
            )}

            <label className="block text-sm">
              <span className="block text-slate-600">Notes</span>
              <textarea
                className="block w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
              />
            </label>

            <div className="flex items-center gap-3">
              <Button type="submit" disabled={create.isPending || update.isPending}>
                {mode === 'create' ? 'Create' : 'Save'}
              </Button>
              {error && (
                <span className="text-sm text-red-600">{(error as Error).message}</span>
              )}
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
